import type { ParsedReceipt } from '@/lib/ocr';

/** Destinations that open a pre-filled FORM (document kinds save
 *  straight to Media & Files inside the scan page instead). */
export type SmartScanFormKind = 'expense' | 'quote' | 'inventory';

/**
 * Hand-off cache for the Smart Scan flow (/scan → /expenses/new or
 * /quotes/new). The scan page OCRs the photo, classifies it, stashes
 * the result here, and routes to the matching form — which consumes
 * (and clears) the cache on mount to pre-fill itself with the photo
 * attached.
 *
 * Module-level singleton on purpose (same pattern as
 * generatedPlanCache in onboarding): router params can't carry a photo
 * URI + full parse payload cleanly, and this never needs to survive a
 * reload.
 */
export interface SmartScanHandoff {
  /** Parsed OCR result — null when Vision failed but the user chose to
   *  file the photo anyway. */
  parsed: ParsedReceipt | null;
  /** Local URI of the scanned photo / picked file (attached to the
   *  saved record). */
  uri: string;
  /** Mime of the attached file — image/jpeg unless a PDF/document was
   *  handed over (Wave 50: quote/invoice PDFs route into the forms). */
  mime?: string | null;
  /** Original filename — lets the receiving form's doc preview detect
   *  format when the mime is generic (Wave 60). */
  name?: string | null;
  kind: SmartScanFormKind;
}

export const smartScanCache: { value: SmartScanHandoff | null } = { value: null };

/** Consume-once read — returns the pending hand-off and clears it. */
export function takeSmartScan(kind: SmartScanFormKind): SmartScanHandoff | null {
  const v = smartScanCache.value;
  if (!v || v.kind !== kind) return null;
  smartScanCache.value = null;
  return v;
}

/**
 * Files shared INTO the app from another Android app (share sheet →
 * ShareIntentBridge). The bridge stashes them here and routes to
 * /scan, which consumes the list and runs the batch pipeline on it.
 * Same singleton rationale as smartScanCache.
 */
export interface SharedFile {
  uri: string;
  name?: string | null;
  mimeType?: string | null;
  size?: number | null;
}

export const sharedFilesCache: { value: SharedFile[] | null } = { value: null };

/** Consume-once read — returns pending shared files and clears them. */
export function takeSharedFiles(): SharedFile[] | null {
  const v = sharedFilesCache.value;
  sharedFilesCache.value = null;
  return v;
}
